/**
 * Configuración → Asignar mangueras (`/configuracion/mangueras`).
 *
 * Puerto de `Maquina::Nozzles()` + `views/menu/maquina/nozzles.php`. Escribe la
 * tabla del controlador PTS (`pts_nozzles`): cada fila es un lado del controlador
 * y sus seis columnas guardan **el producto** de cada manguera. Es exactamente lo
 * que lee **Monitoreo en vivo**, así que cualquier cambio se ve ahí.
 *
 * Reglas conservadas: se piden los seis productos por lado, el guardado deja en
 * blanco las posiciones vacías y "Sincronizar" crea la fila del controlador que
 * le falta a cada lado con número asignado (más el vínculo de operador).
 *
 * Dos diferencias anotadas:
 * - Se corrige el bug del panel anterior: la **sexta manguera** se validaba con el
 *   valor de la quinta, así que con la quinta vacía nunca se guardaba. Cada
 *   posición se lee con su propio índice.
 * - El panel anterior hacía seis `UPDATE` por lado; aquí va uno solo por lado.
 */

import { ArrowLeft, Bot, Check, Droplets, RefreshCw, Save, ShieldAlert, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import {
  useEstadoSincronizacion,
  useMangueras,
  useMutarMangueras,
  useSincronizar,
} from '../avanzado.api';
import type { LadoControlador, ProductoManguera } from '../avanzado.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Convierte las seis mangueras del lado en un arreglo de ids (null = vacía). */
function idsDe(lado: LadoControlador): (number | null)[] {
  const porPosicion = new Map(lado.mangueras.map((manguera) => [manguera.posicion, manguera.producto_id]));

  return [1, 2, 3, 4, 5, 6].map((posicion) => porPosicion.get(posicion) ?? null);
}

/** Producto con su puntito de color para los selectores. */
function etiquetaProducto(producto: ProductoManguera): string {
  return producto.tipo === 'COMBUSTIBLE' ? producto.nombre : `${producto.nombre} (${producto.tipo ?? 'producto'})`;
}

/** Grilla de mangueras: una fila por lado del controlador. */
function ManguerasGrid({
  lados,
  productos,
  guardando,
  onGuardar,
  onDescartar,
  error,
}: {
  lados: LadoControlador[];
  productos: ProductoManguera[];
  guardando: boolean;
  onGuardar: (lados: { pump: number; mangueras: (number | null)[] }[]) => void;
  onDescartar: () => void;
  error: string | null;
}) {
  const inicial = new Map(lados.map((lado) => [lado.pump, idsDe(lado)]));
  const [estado, setEstado] = useState(() => new Map(inicial));

  const cambiar = (pump: number, posicion: number, valor: number | null) => {
    setEstado((actual) => {
      const copia = new Map(actual);
      const fila = [...(copia.get(pump) ?? [null, null, null, null, null, null])];
      fila[posicion - 1] = valor;
      copia.set(pump, fila);

      return copia;
    });
  };

  const sucios = lados.filter((lado) => {
    const antes = inicial.get(lado.pump) ?? [];
    const ahora = estado.get(lado.pump) ?? [];

    return antes.some((valor, indice) => valor !== ahora[indice]);
  });

  const colorDe = (productoId: number | null): string =>
    productos.find((producto) => producto.id === productoId)?.color ?? '#e5e7eb';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mangueras por lado del controlador</CardTitle>
        <Badge variant={sucios.length > 0 ? 'warning' : 'neutral'}>
          {sucios.length > 0 ? `${sucios.length} lado(s) con cambios` : 'Sin cambios'}
        </Badge>
      </CardHeader>
      <CardContent>
        {error ? (
          <Alert variant="error" title="No se pudieron guardar las mangueras" className="mb-4">
            {error}
          </Alert>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[62rem] border-collapse text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                <th className="w-52 pb-2">Lado del controlador</th>
                <th className="w-40 pb-2">En uso por</th>
                {[1, 2, 3, 4, 5, 6].map((posicion) => (
                  <th key={posicion} className="pb-2">
                    Manguera {posicion}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lados.map((lado) => {
                const fila = estado.get(lado.pump) ?? [];
                const antes = inicial.get(lado.pump) ?? [];
                const sucio = antes.some((valor, indice) => valor !== fila[indice]);

                return (
                  <tr
                    key={lado.pump}
                    className={cn('border-t border-ink-100', sucio && 'bg-amber-50/40')}
                  >
                    <td className="py-2 pr-3">
                      <span className="flex flex-col">
                        <span className="flex items-center gap-2 font-medium text-ink-900">
                          Surtidor {lado.pump}
                          {sucio ? <Badge variant="warning">editado</Badge> : null}
                        </span>
                        <span className="text-xs text-ink-500">
                          {lado.lado ?? 'sin lado de máquina'}
                          {lado.lado_activo === false ? ' · lado inactivo' : ''}
                        </span>
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-ink-600">
                      {lado.en_uso_por ?? <span className="text-ink-400">libre</span>}
                    </td>
                    {[1, 2, 3, 4, 5, 6].map((posicion) => {
                      const valor = fila[posicion - 1] ?? null;
                      const manguera = lado.mangueras.find((item) => item.posicion === posicion);
                      const desconocido = manguera?.desconocido && valor !== null;

                      return (
                        <td key={posicion} className="py-2 pr-2">
                          <span className="flex items-center gap-1.5">
                            <span
                              aria-hidden="true"
                              className="size-3 shrink-0 rounded-full ring-1 ring-ink-200"
                              style={{ backgroundColor: colorDe(valor) }}
                            />
                            <Select
                              aria-label={`Manguera ${posicion} del surtidor ${lado.pump}`}
                              className="h-9 w-full rounded-control border border-ink-200 bg-white px-2 text-xs text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                              value={valor === null ? '' : String(valor)}
                              onChange={(evento) =>
                                cambiar(
                                  lado.pump,
                                  posicion,
                                  evento.target.value === '' ? null : Number(evento.target.value),
                                )
                              }
                            >
                              <option value="">— vacía —</option>
                              {desconocido ? (
                                <option value={String(valor)}>
                                  Producto {valor} (no está en el catálogo)
                                </option>
                              ) : null}
                              {productos.map((producto) => (
                                <option key={producto.id} value={producto.id}>
                                  {etiquetaProducto(producto)}
                                </option>
                              ))}
                            </Select>
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-400">
            Al guardar se escriben los seis productos de cada lado modificado. Las posiciones en
            blanco quedan vacías.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" disabled={sucios.length === 0 || guardando} onClick={onDescartar}>
              Descartar
            </Button>
            <Button
              size="sm"
              loading={guardando}
              disabled={sucios.length === 0}
              leftIcon={<Save className="size-3.5" aria-hidden="true" />}
              onClick={() =>
                onGuardar(
                  sucios.map((lado) => ({
                    pump: lado.pump,
                    mangueras: estado.get(lado.pump) ?? [],
                  })),
                )
              }
            >
              Guardar cambios
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ManguerasPage() {
  useDocumentTitle('Configuración · Asignar mangueras');

  const mangueras = useMangueras();
  const estado = useEstadoSincronizacion();
  const guardar = useMutarMangueras();
  const sincronizar = useSincronizar();

  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [version, setVersion] = useState(0);

  const sinPermiso =
    (mangueras.error instanceof ApiError && mangueras.error.status === 403) ||
    (estado.error instanceof ApiError && estado.error.status === 403);

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Droplets className="size-6 text-brand-500" aria-hidden="true" />
          Asignar mangueras
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Qué producto despacha cada manguera del controlador. Esta configuración es la que lee{' '}
          <strong>Monitoreo en vivo</strong> para mostrar los surtidores.
        </p>
      </div>

      <Button
        variant="outline"
        size="sm"
        leftIcon={<RefreshCw className="size-3.5" aria-hidden="true" />}
        loading={sincronizar.isPending}
        disabled={sinPermiso}
        onClick={() => {
          setAviso(null);
          sincronizar.mutate(undefined, {
            onSuccess: (datos) => setAviso({ tono: 'success', texto: datos.mensaje }),
            onError: (fallo) =>
              setAviso({ tono: 'error', texto: mensajeDe(fallo, 'No se pudo sincronizar.') }),
          });
        }}
      >
        Sincronizar lados
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Administrador</strong>, como en el panel anterior:
            escribe la configuración del controlador.
          </span>
        </Alert>
      </div>
    );
  }

  const resumen = estado.data?.resumen;

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Estado de la sincronización</CardTitle>
          {resumen ? (
            <Badge variant={resumen.pendientes_insertar === 0 ? 'success' : 'warning'}>
              {resumen.pendientes_insertar === 0
                ? 'Todo sincronizado'
                : `${resumen.pendientes_insertar} lado(s) pendientes`}
            </Badge>
          ) : null}
        </CardHeader>
        <CardContent>
          {estado.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : estado.error ? (
            <Alert variant="error" title="No se pudo leer el estado">
              {mensajeDe(estado.error, 'La API no respondió.')}
            </Alert>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {(estado.data?.pasos ?? []).map((paso) => (
                  <span
                    key={paso.clave}
                    className={cn(
                      'flex items-start gap-2 rounded-control border px-3 py-2 text-xs',
                      paso.ok
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-amber-200 bg-amber-50 text-amber-900',
                    )}
                  >
                    {paso.ok ? (
                      <Check className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                    ) : (
                      <X className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                    )}
                    <span>
                      <strong className="block font-semibold">{paso.etiqueta}</strong>
                      {paso.detalle}
                    </span>
                  </span>
                ))}
              </div>

              {estado.data && estado.data.pendientes.length > 0 ? (
                <Alert variant="warning" title="Lados sin configurar en el controlador">
                  <span className={ICONO_EN_ALERTA}>
                    <Bot className="size-4" aria-hidden="true" />
                    {estado.data.pendientes
                      .map((pendiente) => `${pendiente.lado} (lado ${pendiente.controlador})`)
                      .join(', ')}
                  </span>
                </Alert>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {mangueras.isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : mangueras.error ? (
        <Alert variant="error" title="No se pudo leer la configuración de mangueras">
          {mensajeDe(mangueras.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <ManguerasGrid
          key={version}
          lados={mangueras.data?.lados ?? []}
          productos={mangueras.data?.productos ?? []}
          guardando={guardar.isPending}
          error={error}
          onDescartar={() => {
            setError(null);
            setVersion((actual) => actual + 1);
          }}
          onGuardar={(lados) => {
            setError(null);
            guardar.mutate(lados, {
              onSuccess: (datos) => {
                setAviso({ tono: 'success', texto: datos.mensaje });
                setVersion((actual) => actual + 1);
              },
              onError: (fallo) => setError(mensajeDe(fallo, 'No se pudieron guardar las mangueras.')),
            });
          }}
        />
      )}

      <Alert variant="info">
        Las mangueras se guardan en la tabla del controlador (<code>pts_nozzles</code>). Los lados
        se dan de alta en <Link to="/configuracion/lados" className="font-semibold underline">Lados de máquinas</Link>{' '}
        y se sincronizan aquí con <strong>Sincronizar lados</strong>.
      </Alert>
    </div>
  );
}
